import { ProjectAdministrationSubpage } from '../../../projectAdministrationLib';
import {
  EntityListContentItem,
  EntityListCustomMessages,
  EntityListTypeMap,
} from '../entityListTypes';
import { EntityListContentStructure } from '../content_structure/EntityListContentStructure';
import { EntityListDescriptor } from '../descriptors/EntityListDescriptor';
import { EntityListItemConverter } from '../converters/EntityListItemConverter';
import { EntityListItemValidator } from '../validators/EntityListItemValidator';
import {
  ImportedItemRow,
  ImportedItemValidator,
  ImportedItemValidatorContext,
  ImportFormatItem,
} from '../../../../../lib/imports/importTypes';
import { InputFormatProvider } from '../input_formats/InputFormatProvider';
import { createValidColumnsCountValidator } from '../../../../../lib/imports/importValidatorsLib';
import { DuplicateItemsValidator } from '../entityListsImportValidatorsLib';
import { EntityListItemType, EntityListTypeName } from '@ydistri/api-sdk';

/**
 * Base class for entity list specifications. The purpose of this class is to provide a common
 * interface for all entity list specifications in one place. It provides descriptive
 * data about the entity list, the content structure, the converter, and the validator.
 * The information is used by generic React components to render the entity list and
 * to validate the data.
 */
export abstract class EntityListSpecification<T extends EntityListTypeMap[keyof EntityListTypeMap]>
  implements
    EntityListDescriptor,
    EntityListItemConverter<T>,
    EntityListContentStructure,
    EntityListItemValidator<T>,
    InputFormatProvider<T>
{
  public readonly subpage: ProjectAdministrationSubpage;
  public readonly pageTitle: string;
  public readonly title: string;
  public readonly menuOptionTitle: string;
  public readonly shortName: string;
  public readonly customMessages: EntityListCustomMessages | undefined;
  public abstract readonly itemType: EntityListItemType;
  public readonly relatedEntityListTypes: EntityListTypeName[] = [];

  protected constructor(
    descriptor: EntityListDescriptor,
    private contentStructure: EntityListContentStructure,
    private converter: EntityListItemConverter<T>,
    private validator: EntityListItemValidator<T>,
    private inputFormatProvider: InputFormatProvider<T>,
  ) {
    this.subpage = descriptor.subpage;
    this.pageTitle = descriptor.pageTitle;
    this.title = descriptor.title;
    this.menuOptionTitle = descriptor.menuOptionTitle;
    this.shortName = descriptor.shortName;
    this.customMessages = descriptor.customMessages;

    if (descriptor.relatedEntityListTypes) {
      this.relatedEntityListTypes = descriptor.relatedEntityListTypes;
    }
  }

  /**
   * Provides the content structure of the entity list so the table displaying items can construct
   * the table header and the table body.
   */
  getContentTableStructure(): EntityListContentItem[] {
    return this.contentStructure.getContentTableStructure();
  }

  /**
   * For AntD table provides a path to the given content item. Path should be provided as an array of strings
   * so the table can use it to display the content item in the table.
   * Each string in the array represents a key in the object provided by API.
   * @param contentItem
   */
  getContentObjectPath(contentItem: EntityListContentItem): string[] {
    return this.contentStructure.getContentObjectPath(contentItem);
  }

  /**
   * The Entity List editor uses this method to convert the data pasted from Excel to the
   * TextArea to parse the data and convert it to a structured format.
   * @param cells
   */
  convertFromNodes(cells: NodeListOf<HTMLTableCellElement>): Partial<T> {
    return this.converter.convertFromNodes(cells);
  }

  /**
   * The Entity List editor uses this method to convert the data pasted in text format
   * to the TextArea to parse the data and convert it to a structured format.
   * @param cells
   */
  convertFromStrings(cells: string[]): Partial<T> {
    return this.converter.convertFromStrings(cells);
  }

  /**
   * The Entity List editor uses this method to construct the API request's body
   * before it is sent. The method should tak array of structured data and return a single string
   * with all the data separated by commas and new lines.
   * @param data
   */
  convertToStructuredText(data: ImportedItemRow<T>[]): string {
    return this.converter.convertToStructuredText(data);
  }

  /**
   * The Entity List editor uses this method to validate the pasted data to determine errors
   * (like missing items or invalid quantity)
   * @param item
   * @param context
   */
  validate(item: Partial<T>, context?: ImportedItemValidatorContext[]): string[] {
    return this.validator.validate(item, context);
  }

  getInputFormat(): readonly ImportFormatItem<T>[] {
    return this.inputFormatProvider.getInputFormat();
  }

  getValidators(context?: ImportedItemValidatorContext[]): ImportedItemValidator<T>[] {
    return this.createValidators(context);
  }

  private createValidators(context?: ImportedItemValidatorContext[]) {
    const result = [
      (item: ImportedItemRow<T>) => {
        return this.validate(item.inputData, context);
      },
      createValidColumnsCountValidator(this.getInputFormat()),
    ];

    const uniqueItems = this.getUniqueItemsForDuplicateCheck();
    if (uniqueItems.length > 0) {
      result.push(new DuplicateItemsValidator(uniqueItems));
    }

    return result;
  }

  abstract getUniqueItemsForDuplicateCheck(): ImportFormatItem<T>[];
}
